import ImageUpload from '@/components/common/ImageUpload'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Pagination from '@/components/ui/Pagination'
import { MultiSelect } from '@/components/ui/multi-select-combobox'
import { api } from '@/lib/api'
import ImpersonateButton from '@/components/impersonation/ImpersonateButton'
import { cn, formatDate, isPresignedUrl } from '@/lib/utils'
import { Agency, Counselor } from '@/types'
import {
  CheckCircle,
  Clock,
  Edit2,
  Eye,
  Filter,
  Mail,
  Plus,
  Power,
  Search,
  Star,
  User,
  XCircle
} from 'lucide-react'
import React, { useEffect, useState } from 'react'

/* Unused interface - commented to fix build
interface CounselorCardProps {
  counselor: Counselor
  onEdit: (counselor: Counselor) => void
  onView: (counselor: Counselor) => void
  onToggleStatus: (counselor: Counselor) => void
}
*/

/* Unused component - commented to fix build
const CounselorCard: React.FC<CounselorCardProps> = ({ 
  counselor, 
  onEdit, 
  onView, 
  onToggleStatus 
}) => {
  const [showDropdown, setShowDropdown] = useState(false)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 bg-green-500 rounded-full flex items-center justify-center">
            {counselor.profilePicture ? (
              <img 
                src={counselor.profilePicture} 
                alt={`${counselor.firstName} ${counselor.lastName}`}
                className="h-12 w-12 rounded-full object-cover"
              />
            ) : (
              <User className="h-6 w-6 text-white" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {counselor.firstName} {counselor.lastName}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                counselor.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              )}>
                {counselor.isActive ? 'Active' : 'Inactive'}
              </span>
              {counselor.isAvailable && counselor.isActive && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Available
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <MoreHorizontal className="h-4 w-4 text-gray-500" />
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <div className="py-1">
                <button
                  onClick={() => {
                    onView(counselor)
                    setShowDropdown(false)
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </button>
                <button
                  onClick={() => {
                    onEdit(counselor)
                    setShowDropdown(false)
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onToggleStatus(counselor)
                    setShowDropdown(false)
                  }}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <Power className="h-4 w-4 mr-2" />
                  {counselor.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2 text-sm text-gray-600 mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Badge className="h-4 w-4 mr-1" />
            <span>{counselor.specialization || 'N/A'}</span>
          </div>
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            <span>{counselor.experienceYears || 0} years exp</span>
          </div>
        </div>
        
        {counselor.email && (
          <div className="flex items-center">
            <Mail className="h-4 w-4 mr-1" />
            <span className="truncate">{counselor.email}</span>
          </div>
        )}
        
        {counselor.phoneNumber && (
          <div className="flex items-center">
            <span className="h-4 w-4 mr-1 text-gray-400">📞</span>
            <span className="truncate">{counselor.phoneNumber}</span>
          </div>
        )}
        
        {counselor.agencyName && (
          <div className="flex items-center">
            <Building2 className="h-4 w-4 mr-1" />
            <span className="truncate">{counselor.agencyName}</span>
          </div>
        )}

        {counselor.qualification && (
          <div className="text-xs text-gray-500 mt-2">
            <strong>Qualification:</strong> {counselor.qualification}
          </div>
        )}

        {counselor.languages && counselor.languages.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {counselor.languages.map((language, index) => (
              <span 
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-800"
              >
                {language}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {formatNumber(counselor.totalSessions || 0)}
          </div>
          <div className="text-xs text-gray-500">Total Sessions</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1">
            <Star className="h-4 w-4 text-yellow-400 fill-current" />
            <span className="text-lg font-semibold text-gray-900">
              {counselor.rating ? counselor.rating.toFixed(1) : 'N/A'}
            </span>
          </div>
          <div className="text-xs text-gray-500">Rating</div>
        </div>
      </div>

      {counselor.hourlyRate && counselor.hourlyRate > 0 && (
        <div className="mt-3 text-center">
          <span className="text-sm font-medium text-gray-900">
            ${counselor.hourlyRate}/hour
          </span>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        Joined: {formatDate(counselor.createdAt)}
      </div>
    </div>
  )
}
*/

const CounselorManagement: React.FC = () => {
  const [counselors, setCounselors] = useState<Counselor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterAvailability, setFilterAvailability] = useState<'all' | 'available' | 'unavailable'>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCounselors, setTotalCounselors] = useState(0)
  const [error, setError] = useState('')
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedCounselor, setSelectedCounselor] = useState<Counselor | null>(null)

  const fetchCounselors = async (page = 1, search = '', status = 'all', availability = 'all') => {
    try {
      setLoading(true)
      const params: any = {
        page,
        limit: 12,
      }
      
      if (search) params.search = search
      if (status !== 'all') params.isActive = status === 'active'
      if (availability !== 'all') params.isAvailable = availability === 'available'

      console.log('Fetching counselors with params:', params)
      
      // Use centralized API call
      const response = await api.get<{data: Counselor[], meta: any}>(`/admin/counselors?${new URLSearchParams(params)}`)
      console.log('Counselor API response:', response)
      
      // Handle the backend response structure
      setCounselors(response.data || [])
      setTotalPages(response.meta?.lastPage || response.meta?.totalPages || 1)
      setTotalCounselors(response.meta?.total ?? 0)
      setError('')
    } catch (error) {
      console.error('Failed to fetch counselors:', error)
      setError(`Failed to load counselors: ${error instanceof Error ? error.message : 'Unknown error'}`)
      // Mock data for development
      setCounselors([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCounselors(currentPage, searchTerm, filterStatus, filterAvailability)
  }, [currentPage, searchTerm, filterStatus, filterAvailability])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleStatusFilterChange = (status: 'all' | 'active' | 'inactive') => {
    setFilterStatus(status)
    setCurrentPage(1)
  }

  const handleAvailabilityFilterChange = (availability: 'all' | 'available' | 'unavailable') => {
    setFilterAvailability(availability)
    setCurrentPage(1)
  }

  const handleAddCounselor = () => {
    setSelectedCounselor(null)
    setShowAddModal(true)
  }

  const handleEditCounselor = async (counselor: Counselor) => {
    try {
      // Fetch full counselor details using the centralized API
      const fullCounselorData = await api.get<Counselor>(`/admin/counselors/${counselor.id}`)
      setSelectedCounselor(fullCounselorData)
    } catch (error) {
      console.error('Failed to fetch counselor details:', error)
      // Fallback to the counselor data we already have
      setSelectedCounselor(counselor)
    }
    
    setShowEditModal(true)
  }

  const handleViewCounselor = async (counselor: Counselor) => {
    try {
      // Fetch full counselor details using the centralized API
      const fullCounselorData = await api.get<Counselor>(`/admin/counselors/${counselor.id}`)
      setSelectedCounselor(fullCounselorData)
    } catch (error) {
      console.error('Failed to fetch counselor details:', error)
      // Fallback to the counselor data we already have
      setSelectedCounselor(counselor)
    }
    
    setShowViewModal(true)
  }

  const handleToggleStatus = async (counselor: Counselor) => {
    const action = counselor.isActive ? 'deactivate' : 'activate'
    
    if (!confirm(`Are you sure you want to ${action} "${counselor.firstName} ${counselor.lastName}"?`)) {
      return
    }

    try {
      // Send the status in the format backend expects
      const newStatus = counselor.isActive ? 'inactive' : 'active'
      
      await api.patch(`/admin/counselors/${counselor.id}/status`, { status: newStatus })
      // Refresh the list
      fetchCounselors(currentPage, searchTerm, filterStatus, filterAvailability)
    } catch (error: any) {
      console.error('Failed to toggle counselor status:', error)
      setError(error.message || 'Failed to update counselor status')
    }
  }

  return (
    <div className="gap-y-6 flex flex-col">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Counselor Management</h1>
        <button
          onClick={handleAddCounselor}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Counselor</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search counselors..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => handleStatusFilterChange(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            
            <select
              value={filterAvailability}
              onChange={(e) => handleAvailabilityFilterChange(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Availability</option>
              <option value="available">Available</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Counselors Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : counselors.length === 0 ? (
          <div className="p-12 text-center">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No counselors found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'all' || filterAvailability !== 'all'
                ? 'Try adjusting your search or filter criteria.' 
                : 'Get started by adding your first counselor.'}
            </p>
            {(!searchTerm && filterStatus === 'all' && filterAvailability === 'all') && (
              <button
                onClick={handleAddCounselor}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 inline-flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add First Counselor</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200 md:hidden">
              {counselors.map((counselor) => (
                <li key={counselor.id} className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      {counselor.profilePicture ? (
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={counselor.profilePicture}
                          alt={`${counselor.firstName} ${counselor.lastName}`}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-green-500 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {counselor.firstName} {counselor.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{counselor.totalSessions || 0} sessions</p>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      <span
                        className={cn(
                          'inline-flex px-2 py-0.5 text-xs font-semibold rounded-full',
                          counselor.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
                        )}
                      >
                        {counselor.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {counselor.isAvailable && counselor.isActive && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Available
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                    {counselor.email && (
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                        {counselor.email}
                      </span>
                    )}
                    {counselor.phoneNumber && <span>{counselor.phoneNumber}</span>}
                    <span>{counselor.experienceYears || 0} yrs exp</span>
                    <span>{counselor.agencyName || 'Independent'}</span>
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-400 fill-current" />
                      {counselor.rating ? counselor.rating.toFixed(1) : 'N/A'}
                    </span>
                  </div>

                  {counselor.specializations.length > 0 && (
                    <p className="text-xs text-gray-500 truncate">
                      {counselor.specializations.join(', ')}
                    </p>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={() => handleViewCounselor(counselor)}
                      className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
                      aria-label="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditCounselor(counselor)}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
                      aria-label="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <ImpersonateButton
                      userId={counselor.userId}
                      name={`${counselor.firstName} ${counselor.lastName}`.trim()}
                      isActive={counselor.isActive}
                    />
                    <button
                      onClick={() => handleToggleStatus(counselor)}
                      className={cn(
                        'p-1.5 rounded-md',
                        counselor.isActive
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50',
                      )}
                      aria-label={counselor.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Counselor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Specializations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Experience
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {counselors.map((counselor) => (
                    <tr key={counselor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {counselor.profilePicture ? (
                              <img 
                                className="h-10 w-10 rounded-full object-cover" 
                                src={counselor.profilePicture} 
                                alt={`${counselor.firstName} ${counselor.lastName}`}
                              />
                            ) : (
                              <div className="h-10 w-10 bg-green-500 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {counselor.firstName} {counselor.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {counselor.totalSessions || 0} sessions
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {counselor.email && (
                            <div className="flex items-center mb-1">
                              <Mail className="h-3 w-3 mr-1 text-gray-400" />
                              <span className="truncate max-w-[130px]" title={counselor.email}>{counselor.email}</span>
                            </div>
                          )}
                          {counselor.phoneNumber && (
                            <div className="flex items-center">
                              <span className="h-3 w-3 mr-1 text-gray-400">📞</span>
                              <span>{counselor.phoneNumber}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {counselor.specializations.join(', ') || 'N/A'}
                        </div>
                        {/* <div className="text-sm text-gray-500">
                          {counselor.qualification || 'N/A'}
                        </div> */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {counselor.experienceYears || 0} years
                        </div>
                        {counselor.languages && counselor.languages.length > 0 && (
                          <div className="text-sm text-gray-500">
                            {counselor.languages.slice(0, 2).join(', ')}
                            {counselor.languages.length > 2 && ` +${counselor.languages.length - 2}`}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {counselor.agencyName || 'Independent'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span className={cn(
                            'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                            counselor.isActive 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          )}>
                            {counselor.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {counselor.isAvailable && counselor.isActive && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              Available
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                          <span className="text-sm text-gray-900">
                            {counselor.rating ? counselor.rating.toFixed(1) : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewCounselor(counselor)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditCounselor(counselor)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <ImpersonateButton
                            userId={counselor.userId}
                            name={`${counselor.firstName} ${counselor.lastName}`.trim()}
                            isActive={counselor.isActive}
                          />
                          <button
                            onClick={() => handleToggleStatus(counselor)}
                            className={cn(
                              'p-1 rounded',
                              counselor.isActive 
                                ? 'text-red-600 hover:text-red-900' 
                                : 'text-green-600 hover:text-green-900'
                            )}
                            title={counselor.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <Power className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                total={totalCounselors}
                pageSize={12}
                itemLabel="counselors"
              />
            </div>
          </>
        )}
      </div>

      {/* Add Counselor Modal */}
      {showAddModal && (
        <CounselorModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            fetchCounselors(currentPage, searchTerm, filterStatus, filterAvailability)
          }}
          title="Add New Counselor"
        />
      )}

      {/* Edit Counselor Modal */}
      {showEditModal && selectedCounselor && (
        <CounselorModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false)
            fetchCounselors(currentPage, searchTerm, filterStatus, filterAvailability)
          }}
          counselor={selectedCounselor}
          title="Edit Counselor"
        />
      )}

      {/* View Counselor Modal */}
      {showViewModal && selectedCounselor && (
        <CounselorViewModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          counselor={selectedCounselor}
        />
      )}
    </div>
  )
}

// Counselor Form Modal Component
interface CounselorModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  counselor?: Counselor
  title: string
}

const CounselorModal: React.FC<CounselorModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  counselor,
  title
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    specializations: [] as string[],
    experienceYears: 0,
    qualification: '',
    languages: [] as string[],
    bio: '',
    hourlyRate: 0,
    agencyId: '',
    profileImage: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [agenciesLoading, setAgenciesLoading] = useState(false)

  // Initialize form data when counselor is provided (edit mode)
  useEffect(() => {
    if (counselor) {
      // Strip +91 prefix from phone number for display in form
      const phoneNumber = counselor.phoneNumber?.startsWith('+91') 
        ? counselor.phoneNumber.slice(3) 
        : counselor.phoneNumber || ''
      
      setFormData({
        firstName: counselor.firstName || '',
        lastName: counselor.lastName || '',
        email: counselor.email || '',
        phoneNumber: phoneNumber,
        specializations: counselor.specializations,
        experienceYears: counselor.experienceYears || 0,
        qualification: counselor.qualification || '',
        languages: counselor.languages || [],
        bio: counselor.bio || '',
        hourlyRate: counselor.hourlyRate || 0,
        agencyId: counselor.agencyId || '',
        profileImage: isPresignedUrl(counselor.profilePicture) ? '' : (counselor.profilePicture || '')
      })  
    }
  }, [counselor])

  // Fetch agencies when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAgencies()
    }
  }, [isOpen])

  const fetchAgencies = async () => {
    try {
      setAgenciesLoading(true)
      const response = await api.get<{data: Agency[], meta: any}>('/admin/agencies', {
        limit: 100,
        status: 'ACTIVE' // Only fetch active agencies
      })
      setAgencies(response.data || [])
    } catch (error) {
      console.error('Failed to fetch agencies:', error)
      setAgencies([]) // Fallback to empty array on error
    } finally {
      setAgenciesLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setFormError('')

    try {
      // Prepare the data in the format expected by the backend
      const submitData = {
        name: `${formData.firstName} ${formData.lastName}`,
        phoneNumber: formData.phoneNumber ? `+91${formData.phoneNumber}` : '',
        email: formData.email || undefined,
        specializations: formData.specializations,
        experience: formData.experienceYears,
        qualifications: formData.qualification ? [formData.qualification] : [],
        bio: formData.bio || undefined,
        languages: formData.languages,
        agencyId: formData.agencyId || undefined,
        profileImage: formData.profileImage || undefined
      }

      if (counselor) {
        // Edit mode - PATCH request
        await api.patch(`/admin/counselors/${counselor.id}`, {
          ...submitData,
          fullName: submitData.name // Backend expects fullName field too
        })
      } else {
        // Create mode - POST request
        if (!formData.phoneNumber) {
          setFormError('Phone number is required for new counselors')
          return
        }
        
        await api.post('/admin/counselors', submitData)
      }
      
      onSuccess()
    } catch (error) {
      console.error(`Failed to ${counselor ? 'update' : 'create'} counselor:`, error)
      setFormError('Network error occurred. Please check your connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="text-red-800 text-sm">{formError}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number {!counselor && '*'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
                    +91
                  </span>
                  <input
                    type="tel"
                    required={!counselor}
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="1234567890"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="example@gmail.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specializations *
                </label>
                <MultiSelect
                  options={[
                    { value: 'MBA', label: 'MBA' },
                    { value: 'MBBS India', label: 'MBBS India' },
                    { value: 'MBBS Abroad', label: 'MBBS Abroad' },
                    { value: 'BTECH', label: 'BTECH' },
                    { value: 'BBA', label: 'BBA' },
                    { value: 'BCOM', label: 'BCOM' },
                  ]}
                  selected={formData.specializations}
                  onChange={(values) => setFormData({ ...formData, specializations: values })}
                  placeholder="Select specializations"
                />
                {formData.specializations.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">Select at least one specialization</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience (Years) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={formData.experienceYears}
                  onChange={(e) => setFormData({ ...formData, experienceYears: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qualification *
              </label>
              <input
                type="text"
                required
                value={formData.qualification}
                onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bio
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <ImageUpload
                label="Profile Picture"
                value={formData.profileImage}
                onChange={(url) => setFormData({ ...formData, profileImage: url })}
                onUploadError={(error) => setFormError(`Profile image upload failed: ${error}`)}
                maxSizeMB={5}
                placeholder="Upload counselor profile picture"
                previewUrl={counselor?.profilePicture}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agency
              </label>
              <select
                value={formData.agencyId}
                onChange={(e) => setFormData({ ...formData, agencyId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                disabled={agenciesLoading}
              >
                <option value="">No Agency (Independent)</option>
                {agencies.map((agency) => (
                  <option key={agency.id} value={agency.id}>
                    {agency.name}
                  </option>
                ))}
              </select>
              {agenciesLoading && (
                <p className="text-xs text-gray-500 mt-1">Loading agencies...</p>
              )}
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hourly Rate ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.hourlyRate}
                onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div> */}

            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : counselor ? 'Update Counselor' : 'Create Counselor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Counselor View Modal Component
interface CounselorViewModalProps {
  isOpen: boolean
  onClose: () => void
  counselor: Counselor
}

const CounselorViewModal: React.FC<CounselorViewModalProps> = ({
  isOpen,
  onClose,
  counselor
}) => {
  const [counselorStats, setCounselorStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (isOpen && counselor.userId) {
      fetchCounselorStats();
    }
  }, [isOpen, counselor]);

  const fetchCounselorStats = async () => {
    try {
      setLoadingStats(true);
      const stats = await api.get(`/profile/counselor/stats/${counselor.userId}`);
      setCounselorStats(stats);
    } catch (error) {
      console.error('Failed to fetch counselor stats:', error);
      setCounselorStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Counselor Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-green-500 rounded-full flex items-center justify-center">
                {counselor.profilePicture ? (
                  <img 
                    src={counselor.profilePicture} 
                    alt={`${counselor.firstName} ${counselor.lastName}`}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-8 w-8 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {counselor.firstName} {counselor.lastName}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    counselor.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  )}>
                    {counselor.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {counselor.isAvailable && counselor.isActive && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Available
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="text-sm text-gray-900">{counselor.email || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <p className="text-sm text-gray-900">{counselor.phoneNumber || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specializations</label>
                <p className="text-sm text-gray-900">{counselor.specializations.join(', ') || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                <p className="text-sm text-gray-900">{counselor.experienceYears || 0} years</p>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Qualification</label>
                <p className="text-sm text-gray-900">{counselor.qualification || 'N/A'}</p>
              </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agency</label>
                <p className="text-sm text-gray-900">{counselor.agencyName || 'N/A'}</p>
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate</label>
                <p className="text-sm text-gray-900">
                  {counselor.hourlyRate ? `$${counselor.hourlyRate}/hour` : 'N/A'}
                </p>
              </div> */}
            </div>

            {counselor.bio && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <p className="text-sm text-gray-900">{counselor.bio}</p>
              </div>
            )}

            {counselor.languages && counselor.languages.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Languages</label>
                <div className="flex flex-wrap gap-1">
                  {counselor.languages.map((language, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {language}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Counselor Session Statistics */}
            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Session Statistics
              </label>
              {loadingStats ? (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : counselorStats ? (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-2xl font-bold text-green-700">
                        {counselorStats.completedSessions || 0}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Completed</p>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Clock className="h-5 w-5 text-yellow-600" />
                      <span className="text-2xl font-bold text-yellow-700">
                        {counselorStats.pendingRequests || 0}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Pending</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <XCircle className="h-5 w-5 text-red-600" />
                      <span className="text-2xl font-bold text-red-700">
                        {counselorStats.sessionsByStatus?.rejected || 0}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Rejected</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  Unable to load session statistics
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">
                  {counselor.totalSessions || 0}
                </div>
                <div className="text-sm text-gray-500">Total Sessions</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="text-lg font-semibold text-gray-900">
                    {counselor.rating ? counselor.rating.toFixed(1) : 'N/A'}
                  </span>
                </div>
                <div className="text-sm text-gray-500">Rating</div>
              </div>
            </div>

            <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
              <p>Joined: {formatDate(counselor.createdAt)}</p>
              <p>Updated: {formatDate(counselor.updatedAt)}</p>
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CounselorManagement